/**
 * The clock the group speaks, from the clock the document stores.
 *
 * Tee times are held as "HH:MM" on a 24-hour clock, and that is the right
 * storage: it sorts as a string, compares without parsing, survives arithmetic
 * on minutes, and carries no timezone or Date object to go wrong. It is also
 * not how anybody on this trip says a tee time. Nobody is off at fifteen
 * twenty; they are off at 3:20.
 *
 * So the conversion lives here, at the edge, and only screens call it. Nothing
 * that sorts, schedules or does arithmetic ever sees the formatted string —
 * which is the whole reason this is a function rather than a change to the
 * stored shape.
 */

/**
 * "15:20" → "3:20pm". Anything unparseable comes back exactly as it went in.
 *
 * Failing open matters: this runs across stored data, and a group with no tee
 * time yet is an ordinary state rather than an error. A formatter that blanked
 * the field, or invented midnight for it, would be lying about the draw.
 */
export function clockTime(hhmm: string | undefined | null): string {
  if (!hhmm) return ''
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(hhmm.trim())
  if (!m) return hhmm

  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return hhmm

  // 0 and 12 both display as 12 — midnight and noon are the two this gets
  // wrong if you reach for `h % 12` on its own.
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${m[2]}${h < 12 ? 'am' : 'pm'}`
}
